// Shared non-component helpers for the Buddy Guarantee list/detail/create
// screens - factored out so BuddyGuaranteesPage.jsx can stay component-only
// (react-refresh/only-export-components), matching applicantShared.js's own
// precedent in this same directory tree.

import { describeApplicationStatus, formatDateTh, resolveApplicantSteps } from "../applicantShared.js";

export { formatDateTh, resolveApplicantSteps };

// Real, matching-value copy of the backend's own MaxActiveAllowed
// (BuddyGuaranteePolicy.MaxActiveBuddiesPerSourcePerson) purely for the
// pre-submit "already at the cap" UX check (disable the create button,
// calm explanation) before a real network round trip - the detail
// response's own `summary.maxActiveAllowed` is what every drawer actually
// renders from, this constant only pre-empts an avoidable 409.
export const MAX_ACTIVE_BUDDIES = 5;

// dbo.WmxWorkers' own WorkerStatus vocabulary (Prompt 097) has exactly one
// real value today (see WorkerProfileStatuses.ProfileCreated on the
// backend) - honest passthrough for anything else this backend might ever
// add later, never a fabricated richer pipeline.
const WORKER_PROFILE_STATUS_META = {
  ProfileCreated: { label: "มี Worker Profile แล้ว", badgeVariant: "success" },
};

/**
 * The real current pipeline status for a person - reads from the same
 * describeApplicationStatus source of truth every other Agent Portal
 * surface uses for a WorkerApplication (never a separately-invented status
 * vocabulary), or the small, honest WorkerStatus map above for a
 * WorkerProfile. Used for both a source person and a real buddy person -
 * e-KYC/Worker Profile/Ready for Match are always rendered as a STATUS
 * badge here, never as a reason someone is excluded from being a buddy
 * candidate.
 */
export function describeSourcePipelineStatus(sourceType, pipelineStatus) {
  if (sourceType === "WorkerApplication") {
    return describeApplicationStatus(pipelineStatus);
  }
  return WORKER_PROFILE_STATUS_META[pipelineStatus] ?? { label: pipelineStatus || "-", badgeVariant: "neutral" };
}

export function describeSourceTypeLabel(sourceType) {
  return sourceType === "WorkerProfile" ? "Worker Profile" : "Quick Registered";
}

/**
 * Prompt 140C: a single roster filter list (never two separate dimensions)
 * matching the real requirement verbatim - ทั้งหมด/ยังไม่มี Buddy/มี Buddy
 * แล้ว/Buddy ครบ 5/Quick Registered/Worker Profile. "QuickRegistered"/
 * "WorkerProfile" map 1:1 to a real backend source (worker-applications /
 * worker-profiles respectively); "All"/"NoBuddy"/"HasBuddy"/"Full" all
 * merge both real sources (see resolveRosterSourceMode) - the buddy-count
 * ones are a client-side refinement of whichever page is currently loaded,
 * never a fabricated org-wide filtered total.
 */
export const ROSTER_FILTERS = [
  { value: "All", label: "ทั้งหมด" },
  { value: "NoBuddy", label: "ยังไม่มี Buddy" },
  { value: "HasBuddy", label: "มี Buddy แล้ว" },
  { value: "Full", label: `Buddy ครบ ${MAX_ACTIVE_BUDDIES}/${MAX_ACTIVE_BUDDIES}` },
  { value: "QuickRegistered", label: "Quick Registered" },
  { value: "WorkerProfile", label: "Worker Profile" },
];

/** Which real backend source(s) a given ROSTER_FILTERS value pulls from - "All" for anything that isn't source-specific. */
export function resolveRosterSourceMode(filterValue) {
  if (filterValue === "QuickRegistered") return "WorkerApplication";
  if (filterValue === "WorkerProfile") return "WorkerProfile";
  return "All";
}

/** True when `count` (a candidate's known active-buddy count, or undefined/null while still loading) matches the given roster filter's own buddy-count rule. Source-only filters (All/QuickRegistered/WorkerProfile) always match here - their narrowing already happened by choosing which source(s) to fetch. */
export function matchesRosterCountFilter(filterValue, count) {
  if (filterValue === "NoBuddy") {
    return count === 0;
  }
  if (filterValue === "HasBuddy") {
    return typeof count === "number" && count > 0;
  }
  if (filterValue === "Full") {
    return typeof count === "number" && count >= MAX_ACTIVE_BUDDIES;
  }
  return true;
}

/**
 * Prompt 140B-R2: the real onboarding-stage value fed to
 * `resolveApplicantSteps` (imported above) to drive the person-detail
 * drawer's "Quick Registered -> e-KYC -> Worker Profile -> Ready for Match"
 * timeline. A WorkerApplication candidate's real `applicationStatus` is
 * used directly - it is the one field `resolveApplicantSteps`'s own doc
 * comment already establishes as always-accurate. A WorkerProfile candidate
 * has no `applicationStatus` of its own (the profile DTO doesn't carry
 * one), but a real Worker Profile row existing at all is only ever created
 * once WorkerApplicationService's own pipeline reaches
 * `WorkerProfileCreated` (Prompt 097) - so that constant is an honest,
 * derived stand-in, never a fabricated status.
 */
export function resolveCandidateTimelineStatus(candidate) {
  return candidate.sourceType === "WorkerProfile" ? "WorkerProfileCreated" : candidate.pipelineStatus;
}

// Presentational relationship note options - the backend stores whatever
// short string is submitted (RelationshipType, max 60 chars, no fixed DB
// vocabulary) as an OPTIONAL label on a real worker-to-worker buddy edge -
// never a name/phone/contact-detail input anymore (Prompt 140C removed
// that free-text form entirely).
export const RELATIONSHIP_OPTIONS = [
  { value: "", label: "ไม่ระบุ" },
  { value: "เพื่อนร่วมงาน", label: "เพื่อนร่วมงาน" },
  { value: "ครอบครัว", label: "ครอบครัว" },
  { value: "เพื่อน", label: "เพื่อน" },
  { value: "ผู้ค้ำประกัน", label: "ผู้ค้ำประกัน" },
  { value: "อื่นๆ", label: "อื่นๆ" },
];

/**
 * Prompt 140B-R1: the real, stable identity of a source person - used to
 * key the buddy-count map (useBuddyActiveCounts.js) and to dedupe/compare
 * candidates across renders. Exactly one of workerApplicationId/
 * workerProfileId is ever set on a real candidate.
 */
export function sourceKey(candidate) {
  return `${candidate.sourceType}:${candidate.workerApplicationId ?? candidate.workerProfileId}`;
}

/**
 * Normalizes one real GET /api/backoffice/worker-applications list item
 * (WorkerApplicationListItemDto) into this feature's common "candidate"
 * shape - a Quick Registered applicant who has not completed e-KYC yet.
 * Every field here is read straight off the real response; nothing is
 * invented.
 */
export function normalizeWorkerApplicationCandidate(item) {
  return {
    sourceType: "WorkerApplication",
    workerApplicationId: item.applicationId,
    workerProfileId: null,
    name: item.fullNameNative || item.fullNameEnglish || "-",
    phoneNumber: item.phoneNumber ?? null,
    nationality: item.nationality ?? null,
    preferredLanguageCode: item.preferredLanguageCode ?? null,
    skillCode: item.skill?.code ?? null,
    skillName: item.skill?.name ?? null,
    passportNumberMasked: item.passportNumberMasked ?? null,
    hasPassportDocument: item.hasPassportDocument ?? false,
    inviteCardMasked: item.inviteCard?.inviteCodeMasked ?? null,
    inviteSourceStatus: item.inviteCard?.sourceStatus ?? null,
    pipelineStatus: item.applicationStatus,
    agentId: item.agent?.agentId ?? null,
    agentCode: item.agent?.agentCode ?? null,
    agentName: item.agent?.displayName ?? null,
    createdAtUtc: item.createdAtUtc ?? null,
  };
}

/**
 * Normalizes one real GET /api/backoffice/worker-profiles list item
 * (WorkerProfileListItemDto) into the same common "candidate" shape - a
 * worker whose Worker Profile already exists (post-e-KYC). Deliberately
 * never reads/exposes the DTO's own `readyForMatch` field - that field is
 * hardcoded `false` on every real response today (see
 * WorkerProfileListItemDto's own backend doc comment: "Ready for Match
 * does not exist anywhere in this codebase yet"), so surfacing it as a
 * real status would be exactly the kind of fake data this feature must
 * never show.
 */
export function normalizeWorkerProfileCandidate(item) {
  return {
    sourceType: "WorkerProfile",
    workerApplicationId: null,
    workerProfileId: item.workerId,
    name: item.worker?.fullNameNative || item.worker?.fullNameEnglish || "-",
    phoneNumber: item.worker?.phoneNumber ?? null,
    nationality: item.worker?.nationality ?? null,
    preferredLanguageCode: item.worker?.preferredLanguageCode ?? null,
    // A Worker Profile list item carries no skill/passport/invite-card
    // fields at all (confirmed against WorkerProfileListItemDto - those only
    // exist on the WorkerApplication side) - honestly null/false rather than
    // guessed, never fabricated to look symmetric with the other source.
    skillCode: null,
    skillName: null,
    passportNumberMasked: null,
    hasPassportDocument: false,
    inviteCardMasked: null,
    inviteSourceStatus: null,
    pipelineStatus: item.workerStatus,
    complianceStatus: item.complianceStatus ?? null,
    agentId: item.agent?.agentId ?? null,
    agentCode: item.agent?.agentCode ?? null,
    agentName: item.agent?.displayName ?? null,
    createdAtUtc: item.createdAtUtc ?? null,
  };
}

export function normalizeCandidate(sourceType, item) {
  return sourceType === "WorkerProfile" ? normalizeWorkerProfileCandidate(item) : normalizeWorkerApplicationCandidate(item);
}

/**
 * Prompt 140C: normalizes the real `buddyPerson` object a list/detail
 * response now carries (BuddyGuaranteePersonDto) - null only for a
 * payload-025-era legacy free-text row with no real person reference at
 * all. Never falls back to a typed-in name/phone for a real worker-to-
 * worker row; a legacy row's own caller is expected to fall back to its
 * `displayName`/`phoneNumber` fields directly instead (this function
 * returns null for it, on purpose, so the two cases are never confused).
 */
export function normalizeBuddyPerson(dto) {
  if (!dto) {
    return null;
  }
  return {
    sourceType: dto.personType,
    workerApplicationId: dto.workerApplicationId ?? null,
    workerProfileId: dto.workerProfileId ?? null,
    name: dto.name || "-",
    phoneNumber: dto.phoneNumber ?? null,
    pipelineStatus: dto.pipelineStatus ?? null,
  };
}

/** True when `candidate` is the exact same real person as `other` (both real candidate-shaped objects) - the self-buddy check the create flow uses to disable a row before ever submitting. */
export function isSamePerson(candidate, other) {
  if (!candidate || !other) {
    return false;
  }
  return candidate.sourceType === other.sourceType && sourceKey(candidate) === sourceKey(other);
}

/** "แสดง 1-20 จาก 134" - real range/total text for a real paginated fetch. Returns null while the total isn't known yet or the page is empty. */
export function formatRange(page, pageSize, totalCount) {
  if (!totalCount) {
    return null;
  }
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalCount);
  return `แสดง ${start}-${end} จาก ${totalCount.toLocaleString("th-TH")}`;
}

/**
 * Prompt 140C: the KPI strip's 5 real figures. `totalPeopleInLine` is the
 * sum of the two real roster totals (worker-applications + worker-
 * profiles totalCount - always fetched regardless of the active filter, so
 * this never drifts based on which filter tab is selected).
 * `networkSummary` is the real GET .../buddy-guarantees list response's own
 * `networkSummary` block (BuddyGuaranteeNetworkSummaryDto) - a live SQL
 * aggregate grouped by source person over ACTIVE edges only, never a
 * per-page client-side guess. "ยังไม่มี Buddy" and the average are the only
 * two figures this function derives (peopleWithBuddy/atOrNearCap are read
 * straight off the real aggregate) - both by combining two independently-
 * real numbers, never fabricating a third.
 */
export function computeRosterKpis({ totalPeopleInLine, networkSummary }) {
  const peopleWithBuddy = networkSummary?.peopleWithActiveBuddyCount ?? 0;
  const totalActiveEdges = networkSummary?.totalActiveBuddyEdgeCount ?? 0;
  const histogram = networkSummary?.activeBuddyCountHistogram ?? [0, 0, 0, 0, 0];
  const noBuddy = Math.max(0, (totalPeopleInLine ?? 0) - peopleWithBuddy);
  const atOrNearCap = (histogram[3] ?? 0) + (histogram[4] ?? 0); // exactly-4 + exactly-5
  const averagePerPerson = totalPeopleInLine > 0 ? totalActiveEdges / totalPeopleInLine : 0;

  return {
    totalPeopleInLine: totalPeopleInLine ?? 0,
    peopleWithBuddy,
    noBuddy,
    averagePerPerson,
    atOrNearCap,
  };
}

/**
 * Prompt 140C: the distribution chart's real 0/5..5/5 buckets. Buckets
 * 1-5 are read directly off the real `activeBuddyCountHistogram` (an exact-
 * count SQL GROUP BY, never a bucketed range guess); bucket 0 is the only
 * derived figure (totalPeopleInLine minus everyone the histogram already
 * accounts for), matching computeRosterKpis's own "noBuddy" derivation.
 */
export function buildDistributionBuckets({ totalPeopleInLine, networkSummary }) {
  const histogram = networkSummary?.activeBuddyCountHistogram ?? [0, 0, 0, 0, 0];
  const peopleWithBuddy = histogram.reduce((sum, count) => sum + count, 0);
  const zeroCount = Math.max(0, (totalPeopleInLine ?? 0) - peopleWithBuddy);

  return [0, 1, 2, 3, 4, 5].map((bucket) => ({
    bucket,
    label: `${bucket}/${MAX_ACTIVE_BUDDIES}`,
    count: bucket === 0 ? zeroCount : histogram[bucket - 1] ?? 0,
  }));
}
